import React, { useState, useEffect } from 'react';
import { GraduationCap, Award, Calendar, MapPin } from 'lucide-react';

const Education = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) =>
      setMousePosition({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const educationData = [
    {
      id: 1,
      degree: "Bachelor of Engineering",
      field: "Information Technology",
      institution: "V.E.S.I.T (Chembur)",
      university: "University of Mumbai",
      date: "2022 - Present",
      status: "Currently Pursuing",
      description: "Final-year Information Technology student focusing on software development, data structures, and modern web technologies.",
      color: "from-white to-gray-400",
      bgColor: "bg-white/5 border-white/10",
      iconBg: "bg-gray-500"
    },
    {
      id: 2,
      degree: "Class 12",
      field: "PCM (Physics, Chemistry, Mathematics)",
      institution: "Merit High School",
      university: "",
      date: "2021 - 2022",
      status: "Completed",
      description: "Achieved 89.4% in Class 12. Successfully cracked JEE Advanced with a rank of 24,000.",
      color: "from-gray-400 to-white",
      bgColor: "bg-white/5 border-white/10",
      iconBg: "bg-gray-600"
    },
    {
      id: 3,
      degree: "Class 10",
      field: "Secondary Education",
      institution: "Merit High School",
      university: "",
      date: "Nov 2020",
      status: "Completed",
      description: "Achieved outstanding 97% marks in Class 10 board examinations.",
      color: "from-white to-gray-500",
      bgColor: "bg-white/5 border-white/10",
      iconBg: "bg-gray-700"
    }
  ];

  return (
    <section className="min-h-screen bg-[#0b0f19] py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[500px] h-[500px] rounded-full bg-white/5 blur-[120px]"
          style={{
            left: mousePosition.x - 250,
            top: mousePosition.y - 250,
            transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
          }}
        />
        <div className="absolute top-20 left-20 w-80 h-80 rounded-full bg-black/5 blur-[100px] animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white/5 blur-[100px] animate-pulse delay-700" />
      </div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-24">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 border border-white/20 rounded-full mb-8 backdrop-blur-xl">
            <GraduationCap className="h-5 w-5 text-white" />
            <span className="text-white text-sm font-bold tracking-widest uppercase">Academic Journey</span>
          </div>

          <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter">
            <span className="text-white">Educational </span>
            <span className="text-white">
              Background
            </span>
          </h2>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto font-medium">
            A chronicle of academic milestones and professional development.
          </p>
        </div>

        {/* Education Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-0.5 w-px h-full bg-gradient-to-b from-white/50 via-white/20 to-transparent"></div>

          <div className="space-y-12 md:space-y-24">
            {educationData.map((edu, index) => (
              <div
                key={edu.id}
                className={`relative flex flex-col md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : ''} items-center group`}
              >
                {/* Timeline Node */}
                <div className={`absolute left-8 md:left-1/2 transform md:-translate-x-1/2 z-10 w-16 h-16 ${edu.iconBg} rounded-3xl flex items-center justify-center shadow-2xl shadow-white/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 border-4 border-[#0b0f19]`}>
                  <GraduationCap className="h-8 w-8 text-white" />
                </div>

                {/* Content Card */}
                <div className={`ml-20 md:ml-0 md:w-5/12 ${index % 2 === 0 ? 'md:mr-auto md:pr-16' : 'md:ml-auto md:pl-16'}`}>
                  <div className={`${edu.bgColor} backdrop-blur-xl rounded-[2rem] p-8 border hover:border-white/30 transition-all duration-500 shadow-xl hover:shadow-white/5`}>
                    {/* Status Badge */}
                    <div className="flex items-center justify-between mb-6">
                      <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black bg-white/10 border border-white/20 uppercase tracking-[0.2em]">
                        {edu.status === 'Currently Pursuing' ? (
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        ) : (
                          <Award className="h-3 w-3 text-white" />
                        )}
                        <span className="text-white">{edu.status}</span>
                      </span>
                    </div>

                    {/* Degree & Field */}
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-2 group-hover:text-white transition-colors">
                      {edu.degree}
                    </h3>

                    {edu.field && (
                      <h4 className="text-lg text-white/80 font-bold mb-4 tracking-tight">
                        {edu.field}
                      </h4>
                    )}

                    {/* Institution & Date */}
                    <div className="flex flex-col gap-3 mb-6">
                      <div className="flex items-center gap-3 text-gray-200">
                        <MapPin className="h-5 w-5 text-white" />
                        <span className="font-bold text-sm">{edu.institution}</span>
                      </div>

                      <div className="flex items-center gap-3 text-gray-300">
                        <Calendar className="h-5 w-5 text-white" />
                        <span className="text-sm font-semibold tracking-wide uppercase">{edu.date}</span>
                      </div>
                    </div>

                    {edu.university && (
                      <p className="text-white/70 text-xs mb-4 font-black tracking-widest uppercase">
                        {edu.university}
                      </p>
                    )}

                    {/* Description */}
                    <p className="text-gray-300 leading-relaxed font-medium text-sm">
                      {edu.description}
                    </p>

                    {/* Decorative line */}
                    <div className={`w-12 h-1.5 bg-gradient-to-r ${edu.color} rounded-full mt-6`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="text-center mt-24">
          <div className="inline-flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/20 rounded-2xl backdrop-blur-xl hover:bg-white/10 transition-colors">
            <Award className="h-6 w-6 text-white" />
            <span className="text-white font-black tracking-tighter uppercase">Always Learning & Growing</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;